// buildLayoutEditorToolbarContext.js

import {
	findParentNodeKey,
	getContainerLabel,
	getContainerOrientation,
	getNodeBlockKey,
	isContainerKind,
} from './pageEditorLayoutUtils';

const TOOLBAR_ABOVE_OFFSET = 30;
const TOOLBAR_BELOW_OFFSET = 8;
const TOOLBAR_EDGE_PADDING = 8;
export const LAYOUT_EDITOR_RICH_TEXT_MENU_BAR_HEIGHT = 20;

export function resolveLayoutEditorToolbarPosition({
	blocksByKey = {},
	inlineRichTextSlotKey = '',
	layout = { nodes: {} },
	rowKey = '',
	toolbarAnchor = {},
	toolbarMeasureState = {},
	toolbarRowKey = '',
	toolbarSlotKey = '',
}) {
	const cleanedRowKey = String(rowKey || '').trim();
	if (!cleanedRowKey) {
		return {
			left: '12px',
			placement: 'below',
			top: '0px',
		};
	}

	const anchorLeft = toolbarAnchor.rowKey === cleanedRowKey ? Number(toolbarAnchor.left) || 0 : 0;
	const anchorTop = toolbarAnchor.rowKey === cleanedRowKey ? Number(toolbarAnchor.top) || 0 : 0;
	const rowWidth = toolbarAnchor.rowKey === cleanedRowKey ? Number(toolbarAnchor.rowWidth) || 0 : 0;
	const anchorX = toolbarAnchor.rowKey === cleanedRowKey ? Number(toolbarAnchor.x) || 0 : 0;
	const anchorY = toolbarAnchor.rowKey === cleanedRowKey ? Number(toolbarAnchor.y) || 0 : 0;
	const slotTop = toolbarAnchor.rowKey === cleanedRowKey ? Number(toolbarAnchor.slotTop) || 0 : 0;
	const slotHeight = toolbarAnchor.rowKey === cleanedRowKey ? Number(toolbarAnchor.slotHeight) || 0 : 0;
	const activeSlotNode = cleanedRowKey === String(toolbarRowKey || '').trim()
		? layout.nodes[String(toolbarSlotKey || '').trim()] || null
		: null;
	const activeSlotBlock = activeSlotNode?.kind === 'block_slot'
		? blocksByKey[getNodeBlockKey(activeSlotNode)] || null
		: null;
	const isRichTextToolbarTarget = String(activeSlotBlock?.type || '').trim().toUpperCase() === 'RICH_TEXT';
	const isInlineRichTextEditing = isRichTextToolbarTarget
		&& String(inlineRichTextSlotKey || '').trim() === String(toolbarSlotKey || '').trim();
	const hasSlotBounds = isRichTextToolbarTarget && slotHeight > 0;
	const toolbarWidth = toolbarMeasureState.rowKey === cleanedRowKey ? Number(toolbarMeasureState.width) || 0 : 0;
	const toolbarHeight = toolbarMeasureState.rowKey === cleanedRowKey ? Number(toolbarMeasureState.height) || 0 : 0;
	// Prefer below until measured so translateY(-100%) cannot clip inside overflow containers
	// (form designer modal / layout canvas) before width/height are known.
	if (!rowWidth || !toolbarWidth) {
		return {
			left: `${anchorLeft + 12}px`,
			placement: 'below',
			top: `${Math.max(0, Math.round(anchorTop + TOOLBAR_BELOW_OFFSET))}px`,
		};
	}

	const gutter = 12;
	const maxLeft = Math.max(gutter, rowWidth - toolbarWidth - gutter);
	const nextLeft = Math.min(
		Math.max(anchorX - (toolbarWidth / 2), gutter),
		maxLeft,
	);
	const slotTopWithinRow = Math.round(anchorTop + slotTop);
	const aboveTop = hasSlotBounds
		? slotTopWithinRow
		: isRichTextToolbarTarget
			? Math.round(anchorTop)
			: Math.round(anchorTop + anchorY - TOOLBAR_ABOVE_OFFSET);
	const shouldPlaceBelow = toolbarHeight > 0
		&& aboveTop < toolbarHeight + TOOLBAR_EDGE_PADDING;

	if (isInlineRichTextEditing) {
		return {
			left: `${Math.round(anchorLeft + nextLeft)}px`,
			placement: 'above',
			top: `${slotTopWithinRow + LAYOUT_EDITOR_RICH_TEXT_MENU_BAR_HEIGHT}px`,
		};
	}

	const placement = shouldPlaceBelow ? 'below' : 'above';
	const nextTop = placement === 'below'
		? (hasSlotBounds
			? Math.round(anchorTop + slotTop + slotHeight + TOOLBAR_BELOW_OFFSET)
			: isRichTextToolbarTarget
				? Math.round(anchorTop) + TOOLBAR_BELOW_OFFSET
				: Math.round(anchorTop + anchorY + TOOLBAR_BELOW_OFFSET))
		: aboveTop;

	return {
		left: `${Math.round(anchorLeft + nextLeft)}px`,
		placement,
		top: `${nextTop}px`,
	};
}

export function buildLayoutEditorToolbarContext({
	getSlotControls,
	handleAddSlotToContainer,
	handleDeleteChildFromContainer,
	handleMoveChildInContainer,
	layout = { nodes: {}, rootNodeKey: '' },
	toolbarRowKey = '',
	toolbarSlotKey = '',
}) {
	const containerKey = String(toolbarRowKey || '').trim();
	if (!containerKey) return null;

	const containerNode = layout.nodes[containerKey] || null;
	if (!containerNode || !isContainerKind(containerNode.kind)) return null;

	const parentKey = findParentNodeKey(layout, containerKey);
	const parentNode = parentKey ? layout.nodes[parentKey] || null : null;
	const siblingCount = Array.isArray(parentNode?.children) ? parentNode.children.length : 0;
	const canDeleteContainer = Boolean(parentKey)
		&& !(parentKey === layout.rootNodeKey && containerNode.kind === 'row' && siblingCount <= 1);
	const containerChildren = Array.isArray(containerNode.children) ? containerNode.children : [];
	const childOrientation = getContainerOrientation(containerNode.kind);
	const containerLabel = getContainerLabel(containerNode.kind);
	const activeChildKey = String(toolbarSlotKey || '').trim();
	const activeSlotIndex = containerChildren.findIndex((childKey) => childKey === activeChildKey);
	const activeSlotNode = activeSlotIndex >= 0 ? layout.nodes[activeChildKey] || null : null;
	const activeSlotControls = activeSlotNode?.kind === 'block_slot'
		? getSlotControls(activeSlotNode, {
			itemIndex: activeSlotIndex,
			onDelete: () => handleDeleteChildFromContainer(containerNode.key, activeSlotNode.key),
			onMoveDown: containerNode.kind === 'stack' && activeSlotIndex < containerChildren.length - 1
				? () => handleMoveChildInContainer(containerNode.key, activeSlotNode.key, 1)
				: null,
			onMoveLeft: containerNode.kind === 'row' && activeSlotIndex > 0
				? () => handleMoveChildInContainer(containerNode.key, activeSlotNode.key, -1)
				: null,
			onMoveRight: containerNode.kind === 'row' && activeSlotIndex < containerChildren.length - 1
				? () => handleMoveChildInContainer(containerNode.key, activeSlotNode.key, 1)
				: null,
			onMoveUp: containerNode.kind === 'stack' && activeSlotIndex > 0
				? () => handleMoveChildInContainer(containerNode.key, activeSlotNode.key, -1)
				: null,
			orientation: childOrientation,
			parentKey: containerNode.key,
		})
		: null;

	const containerToolbarActions = [
		{
			iconKey: containerNode.kind === 'row' ? 'splitscreen_vertical_add' : 'add',
			onClick: () => handleAddSlotToContainer(containerNode.key),
			title: containerNode.kind === 'row' ? 'Add column' : 'Add row',
		},
		canDeleteContainer ? {
			iconKey: 'delete',
			onClick: () => handleDeleteChildFromContainer(parentKey, containerNode.key),
			title: containerNode.kind === 'row' ? 'Delete row' : 'Delete stack',
		} : null,
	].filter(Boolean);

	return {
		activeSlotControls,
		activeSlotNode,
		containerKey,
		containerLabel,
		containerToolbarActions,
	};
}